import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  FileDown,
  FileSpreadsheet,
  List,
  Loader2,
  ShoppingCart,
} from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { toast } from "react-toastify";
import PageHeader from "../../../components/PageHeader";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { useAuth } from "../../../context/AuthContext";
import { canVerTodasLojasUser, hasFullAccessUser } from "../../../lib/screenAccess";
import {
  controleProducaoService,
  RelatorioConsumoResponse,
  SituacaoEstoqueInsight,
} from "../../../services/controleProducaoService";
import { lojaService } from "../../../services/lojaService";
import { Loja } from "../../../types/loja";
import { BrazilDate } from "../../../utils/BrazilDate";
import DetalhamentoModal from "./DetalhamentoModal";
import { groupByUndNome, rotuloUnd } from "./groupByUnd";
import {
  exportRelatorioConsumoPdf,
  exportRelatorioConsumoXlsx,
} from "./relatorioConsumoExport";

function primeiroDiaMesISO(): string {
  const now = new Date();
  return BrazilDate.formatISO(BrazilDate.create(now.getFullYear(), now.getMonth() + 1, 1));
}

function hojeISO(): string {
  return BrazilDate.todayISO();
}

function somarCampo<T>(
  items: T[],
  get: (item: T) => number | null | undefined
): number {
  return items.reduce((acc, item) => acc + (get(item) ?? 0), 0);
}

function formatNum(n: number | null | undefined, und?: string): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const txt = Number.isInteger(n)
    ? n.toLocaleString("pt-BR")
    : n.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
  return und ? `${txt} ${und}` : txt;
}

function labelSituacaoEstoque(
  situacao: SituacaoEstoqueInsight,
  saldoFinal: number,
  undNome: string
): string {
  if (situacao === "sobra") {
    return `Sobrou ${formatNum(Math.abs(saldoFinal), undNome)}`;
  }
  if (situacao === "faltou") {
    return `Faltou ${formatNum(Math.abs(saldoFinal), undNome)}`;
  }
  return "Zerou no último dia";
}

function classesSituacaoEstoque(situacao: SituacaoEstoqueInsight): string {
  if (situacao === "sobra") {
    return "border-emerald-200 bg-emerald-50 text-emerald-800";
  }
  if (situacao === "faltou") {
    return "border-red-200 bg-red-50 text-red-800";
  }
  return "border-amber-200 bg-amber-50 text-amber-800";
}

type LojaOpt = { value: number; label: string };

const LOJA_SELECT_STYLES = {
  ...(SELECT_STYLES as StylesConfig<LojaOpt, false>),
  control: (base, state) => ({
    ...(SELECT_STYLES.control?.(base, state) ?? base),
    minHeight: "38px",
    height: "38px",
  }),
  valueContainer: (base) => ({
    ...base,
    height: "36px",
    padding: "0 8px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "36px",
  }),
} satisfies StylesConfig<LojaOpt, false>;

const CAMPO_FILTRO_CLASS =
  "h-[38px] w-full rounded-md border border-neutral-300 px-3 text-sm";

const ControleProducaoRelatorioConsumoPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = hasFullAccessUser(user);
  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    user?.lojaId
  );
  const [dataInicio, setDataInicio] = useState(primeiroDiaMesISO);
  const [dataFim, setDataFim] = useState(hojeISO);
  const [margemPercent, setMargemPercent] = useState(10);
  const [loading, setLoading] = useState(false);
  const [relatorio, setRelatorio] = useState<RelatorioConsumoResponse | null>(
    null
  );
  const [diaSemanaFiltro, setDiaSemanaFiltro] = useState<number | "">("");
  const [itemMediaFiltro, setItemMediaFiltro] = useState<number | "">("");
  const [undFiltro, setUndFiltro] = useState("");
  const [detalhamentoAberto, setDetalhamentoAberto] = useState(false);

  const lojaOptions = useMemo(
    () => (podeVerTodasLojas ? lojas : lojas.filter((l) => l.id === user?.lojaId)),
    [podeVerTodasLojas, lojas, user?.lojaId]
  );

  const lojaSelectOptions = useMemo<LojaOpt[]>(
    () => lojaOptions.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojaOptions]
  );

  const selectedLojaOption =
    lojaSelectOptions.find((o) => o.value === selectedLojaId) ?? null;

  useEffect(() => {
    void lojaService
      .getLojas()
      .then(setLojas)
      .catch(() => setLojas([]));
  }, []);

  useEffect(() => {
    if (lojaOptions.length === 0) return;
    const exists = lojaOptions.some((l) => l.id === selectedLojaId);
    if (!exists) {
      setSelectedLojaId(lojaOptions[0]?.id ?? user?.lojaId);
    }
  }, [lojaOptions, selectedLojaId, user?.lojaId]);

  const carregar = useCallback(async () => {
    if (!selectedLojaId) return;
    setLoading(true);
    try {
      const data = await controleProducaoService.getRelatorioConsumo({
        dataInicio,
        dataFim,
        lojaId: selectedLojaId,
        margemCompraPercent: margemPercent,
      });
      setRelatorio(data);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Erro ao carregar relatório.";
      toast.error(message);
      setRelatorio(null);
    } finally {
      setLoading(false);
    }
  }, [selectedLojaId, dataInicio, dataFim, margemPercent]);

  const linhasVisiveis = useMemo(() => {
    if (!relatorio) return [];
    return relatorio.linhas;
  }, [relatorio]);

  const mediasPorDiaSemana = useMemo(
    () => relatorio?.mediasPorDiaSemana ?? [],
    [relatorio]
  );

  const diasSemanaOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of mediasPorDiaSemana) {
      if (!map.has(m.diaSemana)) map.set(m.diaSemana, m.diaSemanaLabel);
    }
    return Array.from(map.entries())
      .map(([valor, label]) => ({ valor, label }))
      .sort((a, b) => a.valor - b.valor);
  }, [mediasPorDiaSemana]);

  const itensMediaOptions = useMemo(() => {
    const base =
      diaSemanaFiltro === ""
        ? mediasPorDiaSemana
        : mediasPorDiaSemana.filter((m) => m.diaSemana === diaSemanaFiltro);
    const map = new Map<number, string>();
    for (const m of base) {
      if (undFiltro && rotuloUnd(m.undNome) !== undFiltro) continue;
      if (!map.has(m.itemId)) map.set(m.itemId, m.descricao);
    }
    return Array.from(map.entries())
      .map(([id, descricao]) => ({ id, descricao }))
      .sort((a, b) => a.descricao.localeCompare(b.descricao, "pt-BR"));
  }, [mediasPorDiaSemana, diaSemanaFiltro, undFiltro]);

  const undOptions = useMemo(() => {
    const set = new Set<string>();
    for (const ins of relatorio?.insights ?? []) {
      set.add(rotuloUnd(ins.undNome));
    }
    for (const m of mediasPorDiaSemana) {
      set.add(rotuloUnd(m.undNome));
    }
    for (const l of linhasVisiveis) {
      set.add(rotuloUnd(l.undNome));
    }
    return groupByUndNome(
      Array.from(set).map((undNome) => ({ undNome })),
      (x) => x.undNome
    ).map((g) => g.undNome);
  }, [relatorio?.insights, mediasPorDiaSemana, linhasVisiveis]);

  const insightsFiltrados = useMemo(() => {
    const lista = relatorio?.insights ?? [];
    if (!undFiltro) return lista;
    return lista.filter((ins) => rotuloUnd(ins.undNome) === undFiltro);
  }, [relatorio?.insights, undFiltro]);

  const insightsPorUnd = useMemo(
    () => groupByUndNome(insightsFiltrados, (ins) => ins.undNome),
    [insightsFiltrados]
  );

  const mediasFiltradas = useMemo(() => {
    return mediasPorDiaSemana.filter((m) => {
      if (diaSemanaFiltro !== "" && m.diaSemana !== diaSemanaFiltro) {
        return false;
      }
      if (itemMediaFiltro !== "" && m.itemId !== itemMediaFiltro) {
        return false;
      }
      if (undFiltro && rotuloUnd(m.undNome) !== undFiltro) {
        return false;
      }
      return true;
    });
  }, [mediasPorDiaSemana, diaSemanaFiltro, itemMediaFiltro, undFiltro]);

  const mediasPorUnd = useMemo(
    () => groupByUndNome(mediasFiltradas, (m) => m.undNome),
    [mediasFiltradas]
  );

  const ocorrenciasFiltradas = useMemo(() => {
    return linhasVisiveis
      .filter((l) => {
        if (diaSemanaFiltro !== "" && l.diaSemana !== diaSemanaFiltro) {
          return false;
        }
        if (itemMediaFiltro !== "" && l.itemId !== itemMediaFiltro) {
          return false;
        }
        if (undFiltro && rotuloUnd(l.undNome) !== undFiltro) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        const da = a.dataFicha ?? a.periodoKey;
        const db = b.dataFicha ?? b.periodoKey;
        if (da !== db) return da.localeCompare(db);
        return a.descricao.localeCompare(b.descricao, "pt-BR");
      });
  }, [linhasVisiveis, diaSemanaFiltro, itemMediaFiltro, undFiltro]);

  const ocorrenciasPorUnd = useMemo(
    () => groupByUndNome(ocorrenciasFiltradas, (l) => l.undNome),
    [ocorrenciasFiltradas]
  );

  useEffect(() => {
    setDiaSemanaFiltro("");
    setItemMediaFiltro("");
    setUndFiltro("");
  }, [relatorio?.lojaId, relatorio?.dataInicio, relatorio?.dataFim]);

  useEffect(() => {
    if (itemMediaFiltro === "") return;
    const itemValido = itensMediaOptions.some((i) => i.id === itemMediaFiltro);
    if (!itemValido) setItemMediaFiltro("");
  }, [itemMediaFiltro, itensMediaOptions]);

  const lojaNomeRelatorio = useMemo(() => {
    return (
      lojaOptions.find((l) => l.id === relatorio?.lojaId)?.nome ??
      lojaOptions.find((l) => l.id === selectedLojaId)?.nome ??
      "—"
    );
  }, [lojaOptions, relatorio?.lojaId, selectedLojaId]);

  const podeExportar = useMemo(() => {
    if (!relatorio) return false;
    return (
      relatorio.insights.length > 0 ||
      relatorio.mediasPorDiaSemana.length > 0
    );
  }, [relatorio]);

  const buildExportInput = useCallback(() => {
    if (!relatorio) return null;
    return {
      lojaNome: lojaNomeRelatorio,
      periodoInicioISO: relatorio.dataInicio,
      periodoFimISO: relatorio.dataFim,
      margemCompraPercent: relatorio.margemCompraPercent,
      diasAbertosNoPeriodo: relatorio.diasAbertosNoPeriodo,
      insights: relatorio.insights,
      mediasPorDiaSemana: relatorio.mediasPorDiaSemana,
      geradoEmLabel: BrazilDate.formatBR(new Date(), true),
    };
  }, [relatorio, lojaNomeRelatorio]);

  const handleExportExcel = useCallback(() => {
    const input = buildExportInput();
    if (!input) {
      toast.warning("Gere o relatório antes de exportar.");
      return;
    }
    try {
      exportRelatorioConsumoXlsx(input);
      toast.success("Planilha Excel baixada.");
    } catch {
      toast.error("Não foi possível gerar o Excel.");
    }
  }, [buildExportInput]);

  const handleExportPdf = useCallback(() => {
    const input = buildExportInput();
    if (!input) {
      toast.warning("Gere o relatório antes de exportar.");
      return;
    }
    try {
      exportRelatorioConsumoPdf(input);
      toast.success("PDF baixado.");
    } catch {
      toast.error("Não foi possível gerar o PDF.");
    }
  }, [buildExportInput]);

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Relatório de Consumo e Produção"
        description="Produção, sobra e consumo calculado por período. Use os insights para estimar compras com margem de segurança."
        actions={
          relatorio && !loading ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={!podeExportar}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-white px-3 py-2 text-sm font-semibold text-emerald-800 shadow-sm transition-opacity hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileSpreadsheet size={18} />
                Excel
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={!podeExportar}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileDown size={18} />
                PDF (A4)
              </button>
            </div>
          ) : null
        }
      />

      <div className="mb-6 grid gap-3 rounded-lg border border-neutral-200 bg-white p-4 sm:grid-cols-2 sm:items-end lg:flex lg:flex-row lg:items-end lg:gap-3">
        <div className="flex min-w-0 flex-col gap-1.5 lg:flex-1">
          <label
            htmlFor="relatorio-consumo-loja"
            className="text-sm font-medium leading-tight text-neutral-700"
          >
            Loja
          </label>
          <ReactSelect<LojaOpt, false>
            inputId="relatorio-consumo-loja"
            value={selectedLojaOption}
            onChange={(option: SingleValue<LojaOpt>) =>
              setSelectedLojaId(option?.value)
            }
            options={lojaSelectOptions}
            isClearable={isAdmin}
            placeholder="Selecionar loja"
            styles={LOJA_SELECT_STYLES}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5 lg:flex-1">
          <label
            htmlFor="relatorio-consumo-inicio"
            className="text-sm font-medium leading-tight text-neutral-700"
          >
            De
          </label>
          <input
            id="relatorio-consumo-inicio"
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            className={CAMPO_FILTRO_CLASS}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5 lg:flex-1">
          <label
            htmlFor="relatorio-consumo-fim"
            className="text-sm font-medium leading-tight text-neutral-700"
          >
            Até
          </label>
          <input
            id="relatorio-consumo-fim"
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            className={CAMPO_FILTRO_CLASS}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5 lg:flex-1">
          <label
            htmlFor="relatorio-consumo-margem"
            className="text-sm font-medium leading-tight text-neutral-700"
          >
            Margem compra (%)
          </label>
          <input
            id="relatorio-consumo-margem"
            type="number"
            min={0}
            max={100}
            step={1}
            value={margemPercent}
            onChange={(e) => setMargemPercent(Number(e.target.value) || 0)}
            className={CAMPO_FILTRO_CLASS}
          />
        </div>
        <button
          type="button"
          onClick={() => void carregar()}
          disabled={loading || !selectedLojaId}
          className="h-[38px] w-full shrink-0 rounded-md bg-primary px-4 text-sm font-semibold text-white disabled:opacity-60 sm:col-span-2 lg:w-auto lg:min-w-[9.5rem]"
        >
          {loading ? "Carregando..." : "Gerar relatório"}
        </button>
      </div>

      <p className="mb-4 text-xs text-neutral-500">
        Consumo = estoque inicial (sobra do dia anterior aberto) + produção −
        sobra do dia. Dias &quot;Não abrem&quot; são ignorados. Sugestão de
        compra aplica +{margemPercent}% sobre o consumo médio.
      </p>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Calculando relatório...
        </div>
      )}

      {relatorio && !loading && (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-neutral-200 bg-white p-4">
              <p className="text-xs font-medium uppercase text-neutral-500">
                Dias abertos no período
              </p>
              <p className="mt-1 text-2xl font-bold text-neutral-900">
                {relatorio.diasAbertosNoPeriodo}
              </p>
            </div>
            <div className="rounded-lg border border-neutral-200 bg-white p-4">
              <p className="text-xs font-medium uppercase text-neutral-500">
                Itens cadastrados
              </p>
              <p className="mt-1 text-2xl font-bold text-neutral-900">
                {relatorio.insights.length}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500">
                {
                  relatorio.insights.filter((i) => i.diasComConsumo > 0)
                    .length
                }{" "}
                com consumo no período
              </p>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs font-medium uppercase text-emerald-800">
                Margem de compra
              </p>
              <p className="mt-1 text-2xl font-bold text-emerald-900">
                +{relatorio.margemCompraPercent}%
              </p>
            </div>
          </div>

          {linhasVisiveis.length > 0 && (
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-neutral-900">
                  Detalhamento completo
                </p>
                <p className="text-sm text-neutral-600">
                  {linhasVisiveis.length.toLocaleString("pt-BR")} linhas no
                  período (todos os itens × dias abertos) — filtros e exportação
                  separados do resumo.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDetalhamentoAberto(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold text-neutral-800 shadow-sm hover:bg-neutral-100"
              >
                <List size={18} />
                Abrir detalhamento
              </button>
            </div>
          )}

          {insightsPorUnd.length > 0 && (
            <section className="mb-8">
              <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-neutral-900">
                <ShoppingCart className="h-5 w-5 text-primary" />
                Insights para compra
              </h2>
              <p className="mb-4 text-sm text-neutral-600">
                Quantidades ficam separadas por unidade de medida — kg não se
                mistura com un.
              </p>
              <div className="space-y-6">
                {insightsPorUnd.map((grupo) => (
                  <div key={grupo.undNome}>
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-800">
                      <span className="rounded-full bg-neutral-900 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-white">
                        {grupo.undNome}
                      </span>
                      <span className="text-neutral-500">
                        {grupo.items.length}{" "}
                        {grupo.items.length === 1 ? "item" : "itens"}
                      </span>
                      <span className="ml-auto text-xs font-medium normal-case text-neutral-500">
                        Consumo {formatNum(
                          somarCampo(grupo.items, (i) => i.consumoTotal),
                          grupo.undNome
                        )}
                      </span>
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {grupo.items.map((ins) => (
                        <div
                          key={ins.itemId}
                          className="rounded-lg border border-neutral-200 bg-white p-4"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-semibold text-neutral-900">
                              {ins.descricao}
                            </p>
                            <span
                              className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-semibold ${classesSituacaoEstoque(ins.situacaoEstoque)}`}
                            >
                              {labelSituacaoEstoque(
                                ins.situacaoEstoque,
                                ins.saldoFinal,
                                ins.undNome
                              )}
                            </span>
                          </div>
                          <dl className="mt-2 space-y-1 text-sm text-neutral-600">
                            {ins.estoqueInicialPeriodo != null && (
                              <div className="flex justify-between gap-2">
                                <dt>Estoque inicial</dt>
                                <dd>
                                  {formatNum(
                                    ins.estoqueInicialPeriodo,
                                    ins.undNome
                                  )}
                                </dd>
                              </div>
                            )}
                            <div className="flex justify-between gap-2">
                              <dt>Produção total</dt>
                              <dd className="font-medium text-neutral-800">
                                {formatNum(ins.producaoTotal, ins.undNome)}
                              </dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt>Consumo total</dt>
                              <dd className="font-medium text-neutral-800">
                                {formatNum(ins.consumoTotal, ins.undNome)}
                              </dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt>Sobra (último dia)</dt>
                              <dd>
                                {ins.sobraFinal != null
                                  ? formatNum(ins.sobraFinal, ins.undNome)
                                  : "—"}
                              </dd>
                            </div>
                            {(ins.diasComSobra > 0 ||
                              ins.diasZerouSobra > 0) && (
                              <div className="flex justify-between gap-2 text-xs">
                                <dt>Dias com / sem sobra</dt>
                                <dd>
                                  {ins.diasComSobra} / {ins.diasZerouSobra}
                                </dd>
                              </div>
                            )}
                            <div className="flex justify-between gap-2">
                              <dt>Média/dia</dt>
                              <dd>
                                {formatNum(ins.consumoMedioDia, ins.undNome)}
                              </dd>
                            </div>
                            <div className="flex justify-between gap-2 border-t border-neutral-100 pt-1">
                              <dt className="font-medium text-emerald-800">
                                Sugestão/dia (+{relatorio.margemCompraPercent}%)
                              </dt>
                              <dd className="font-bold text-emerald-800">
                                {formatNum(
                                  ins.sugestaoCompraDia,
                                  ins.undNome
                                )}
                              </dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt>Sugestão período</dt>
                              <dd className="font-medium">
                                {formatNum(
                                  ins.sugestaoCompraPeriodo,
                                  ins.undNome
                                )}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {mediasPorDiaSemana.length > 0 && (
            <section className="mb-8">
              <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-neutral-900">
                <CalendarDays className="h-5 w-5 text-primary" />
                Consumo por dia da semana
              </h2>
              <p className="mb-4 text-sm text-neutral-600">
                Cada linha abaixo é um dia real do período. O estoque inicial
                vem da sobra do dia anterior aberto; consumo = estoque inicial +
                produção − sobra. As médias no final são só referência quando há
                várias ocorrências do mesmo dia (ex.: três segundas no mês).
              </p>

              {(diasSemanaOptions.length > 0 ||
                itensMediaOptions.length > 0 ||
                undOptions.length > 0) && (
                <div className="mb-4 grid gap-3 sm:grid-cols-3">
                  {undOptions.length > 0 && (
                    <label className="text-sm font-medium text-neutral-700">
                      Unidade de medida
                      <select
                        value={undFiltro}
                        onChange={(e) => setUndFiltro(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2"
                      >
                        <option value="">Todas as unidades</option>
                        {undOptions.map((und) => (
                          <option key={und} value={und}>
                            {und}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {diasSemanaOptions.length > 0 && (
                    <label className="text-sm font-medium text-neutral-700">
                      Filtrar dia
                      <select
                        value={diaSemanaFiltro}
                        onChange={(e) =>
                          setDiaSemanaFiltro(
                            e.target.value ? Number(e.target.value) : ""
                          )
                        }
                        className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2"
                      >
                        <option value="">Todos os dias</option>
                        {diasSemanaOptions.map((dia) => (
                          <option key={dia.valor} value={dia.valor}>
                            {dia.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {itensMediaOptions.length > 0 && (
                    <label className="text-sm font-medium text-neutral-700">
                      Filtrar item
                      <select
                        value={itemMediaFiltro}
                        onChange={(e) =>
                          setItemMediaFiltro(
                            e.target.value ? Number(e.target.value) : ""
                          )
                        }
                        className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2"
                      >
                        <option value="">Todos os itens</option>
                        {itensMediaOptions.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.descricao}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
              )}

              <h3 className="mb-2 text-sm font-semibold text-neutral-800">
                Ocorrências no período
                {diaSemanaFiltro !== "" || itemMediaFiltro !== "" || undFiltro
                  ? " (filtrado)"
                  : ""}
              </h3>
              <div className="mb-6 overflow-x-auto rounded-lg border border-neutral-200 bg-white">
                <table className="min-w-full text-sm">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Data</th>
                      <th className="px-3 py-2 text-left font-semibold">Dia</th>
                      <th className="px-3 py-2 text-left font-semibold">Item</th>
                      <th className="px-3 py-2 text-right font-semibold">
                        Est. inicial
                      </th>
                      <th className="px-3 py-2 text-right font-semibold">
                        Produção
                      </th>
                      <th className="px-3 py-2 text-right font-semibold">Sobra</th>
                      <th className="px-3 py-2 text-right font-semibold">
                        Consumo
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ocorrenciasFiltradas.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-3 py-6 text-center text-neutral-500"
                        >
                          Nenhuma ocorrência para os filtros selecionados.
                        </td>
                      </tr>
                    ) : (
                      ocorrenciasPorUnd.map((grupo) => (
                        <React.Fragment key={grupo.undNome}>
                          <tr className="border-t border-neutral-200 bg-neutral-100">
                            <td
                              colSpan={7}
                              className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-neutral-700"
                            >
                              Unidade {grupo.undNome}
                              <span className="ml-2 font-medium normal-case text-neutral-500">
                                {grupo.items.length}{" "}
                                {grupo.items.length === 1
                                  ? "lançamento"
                                  : "lançamentos"}
                              </span>
                            </td>
                          </tr>
                          {grupo.items.map((linha, idx) => (
                            <tr
                              key={`${linha.periodoKey}-${linha.itemId}-${idx}`}
                              className="border-t border-neutral-100"
                            >
                              <td className="px-3 py-2 whitespace-nowrap">
                                {linha.periodoLabel}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {linha.diaSemanaLabel ?? "—"}
                              </td>
                              <td className="px-3 py-2">{linha.descricao}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-neutral-600">
                                {formatNum(
                                  linha.estoqueInicial,
                                  linha.undNome
                                )}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {formatNum(linha.producao, linha.undNome)}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {formatNum(linha.sobra, linha.undNome)}
                              </td>
                              <td className="px-3 py-2 text-right font-semibold tabular-nums text-primary">
                                {formatNum(linha.consumo, linha.undNome)}
                              </td>
                            </tr>
                          ))}
                          <tr className="border-t border-neutral-200 bg-neutral-50">
                            <td
                              colSpan={4}
                              className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600"
                            >
                              Total {grupo.undNome}
                            </td>
                            <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums">
                              {formatNum(
                                somarCampo(grupo.items, (l) => l.producao),
                                grupo.undNome
                              )}
                            </td>
                            <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums">
                              {formatNum(
                                somarCampo(grupo.items, (l) => l.sobra),
                                grupo.undNome
                              )}
                            </td>
                            <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-primary">
                              {formatNum(
                                somarCampo(grupo.items, (l) => l.consumo),
                                grupo.undNome
                              )}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <h3 className="mb-2 text-sm font-semibold text-neutral-800">
                Médias de referência
              </h3>
              <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white">
                <table className="min-w-full text-sm">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Dia</th>
                      <th className="px-3 py-2 text-left font-semibold">Item</th>
                      <th className="px-3 py-2 text-right font-semibold">
                        Consumo médio
                      </th>
                      <th className="px-3 py-2 text-right font-semibold">
                        Consumo total
                      </th>
                      <th className="px-3 py-2 text-right font-semibold">
                        Produção média
                      </th>
                      <th className="px-3 py-2 text-right font-semibold">
                        Sobra média
                      </th>
                      <th className="px-3 py-2 text-right font-semibold">
                        Sugestão (+{relatorio.margemCompraPercent}%)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {mediasFiltradas.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-3 py-6 text-center text-neutral-500"
                        >
                          Nenhuma média para os filtros selecionados.
                        </td>
                      </tr>
                    ) : (
                      mediasPorUnd.map((grupo) => (
                        <React.Fragment key={grupo.undNome}>
                          <tr className="border-t border-neutral-200 bg-neutral-100">
                            <td
                              colSpan={7}
                              className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-neutral-700"
                            >
                              Unidade {grupo.undNome}
                            </td>
                          </tr>
                          {grupo.items.map((m) => (
                            <tr
                              key={`${m.diaSemana}-${m.itemId}`}
                              className="border-t border-neutral-100"
                            >
                              <td className="px-3 py-2 whitespace-nowrap">
                                {m.diaSemanaLabel}
                              </td>
                              <td className="px-3 py-2">{m.descricao}</td>
                              <td className="px-3 py-2 text-right font-semibold tabular-nums text-primary">
                                {formatNum(m.consumoMedio, m.undNome)}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {formatNum(m.consumoTotal, m.undNome)}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {formatNum(m.producaoMedia, m.undNome)}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {formatNum(m.sobraMedia, m.undNome)}
                              </td>
                              <td className="px-3 py-2 text-right font-medium tabular-nums text-emerald-800">
                                {formatNum(m.sugestaoCompra, m.undNome)}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <DetalhamentoModal
            isOpen={detalhamentoAberto}
            onClose={() => setDetalhamentoAberto(false)}
            linhas={linhasVisiveis}
            lojaNome={lojaNomeRelatorio}
            periodoInicioISO={relatorio.dataInicio}
            periodoFimISO={relatorio.dataFim}
          />
        </>
      )}
    </main>
  );
};

export default ControleProducaoRelatorioConsumoPage;
